// Admin — editor articolo (italiano, tipo Word).
// Nessuna esposizione di HTML/JSON/markdown/percorsi storage/UTC.
// Workflow admin: Nuovo articolo → scrivi → immagini → Anteprima → Pubblica.

import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/modules/growing-circle/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import TipTapEditor from "@/modules/growing-circle/components/TipTapEditor";
import { Eye, Send, Save, Trash2, X, EyeOff, Archive } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const emptyDoc = { type: "doc", content: [{ type: "paragraph" }] };

// Convert a legacy plain-string opening_note into a TipTap document. Each
// non-empty line becomes its own paragraph so the user can format words
// individually with the new inline editor.
function stringToDoc(s) {
  if (!s || typeof s !== "string") return emptyDoc;
  const paras = s.split(/\r?\n/).map((line) => line.trim() ? { type: "paragraph", content: [{ type: "text", text: line }] } : { type: "paragraph" });
  return { type: "doc", content: paras.length ? paras : [{ type: "paragraph" }] };
}

function emptyFields() {
  return {
    series_name: "Self Doubling Growing Circle", title: "", subtitle: "",
    author_name: "Maria Gioia Mosca", author_credentials: "",
    opening_note: emptyDoc, body: emptyDoc,
    author_note_or_biography: "", copyright_note: "",
    cover_media_id: null, cover_alt_text: "", cover_decorative: false,
  };
}

// Convert a JS Date-input value (in Europe/Rome for UI) → UTC ISO for the backend.
function toUtcIso(local) {
  if (!local) return null;
  const dt = new Date(local);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
}
function fromUtcIsoToLocal(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export default function AdminArticleEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const [article, setArticle] = useState(null);
  const [fields, setFields] = useState(emptyFields());
  const [slug, setSlug] = useState("");
  const [publishAt, setPublishAt] = useState("");
  const [moderation, setModeration] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/admin/articles/${id}`);
      setArticle(data);
      // If a draft revision is active (published article being edited), prefer its content.
      let f = data.fields || emptyFields();
      if (data.draft_revision_id) {
        const preview = await api.get(`/admin/articles/${id}/preview`);
        const revContent = preview?.data?.revision?.content;
        if (revContent) f = { ...f, ...revContent };
      }
      setFields({ ...emptyFields(), ...f, opening_note: typeof f.opening_note === "string" ? stringToDoc(f.opening_note) : (f.opening_note || emptyDoc) });
      setSlug(data.slug);
      setPublishAt(fromUtcIsoToLocal(data.publish_at));
      setModeration(!!data.moderation_enabled);
    } catch { toast.error("Impossibile caricare l'articolo."); }
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const uploadCover = async (file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/articles/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFields({ ...fields, cover_media_id: data.media_id });
      toast.success("Copertina caricata.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Caricamento copertina fallito.");
    }
  };

  const validateBeforePublish = () => {
    if (!fields.title.trim()) { toast.error("Il titolo è obbligatorio."); return false; }
    if (!fields.cover_media_id) { toast.error("Aggiungi una copertina prima di pubblicare."); return false; }
    if (!fields.cover_decorative && !fields.cover_alt_text.trim()) {
      toast.error("Testo alternativo copertina obbligatorio (o spunta 'immagine decorativa')."); return false;
    }
    return true;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = { fields, publish_at: toUtcIso(publishAt), moderation_enabled: moderation };
      if (isNew) {
        if (slug.trim()) payload.slug = slug.trim();
        const { data } = await api.post("/admin/articles", payload);
        toast.success("Bozza salvata.");
        nav(`/growing-circle/admin/articles/${data.id}/edit`, { replace: true });
      } else {
        if (slug.trim() && slug.trim() !== article?.slug && !article?.first_published_at) payload.slug = slug.trim();
        await api.put(`/admin/articles/${id}`, payload);
        toast.success("Modifiche salvate.");
        await load();
      }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!validateBeforePublish()) return;
    await saveDraft();
    if (!id && !article?.id) return;
    const aid = id || article?.id;
    try {
      await api.post(`/admin/articles/${aid}/publish`);
      toast.success("Articolo pubblicato.");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const doAction = async (action, confirm) => {
    if (confirm && !window.confirm(confirm)) return;
    try { await api.post(`/admin/articles/${id}/${action}`); toast.success("Fatto."); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const openPreview = () => window.open(`/growing-circle/admin/articles/${id || article?.id}/preview`, "_blank");

  const state = article?.state;
  const canPublishBtnLabel = state === "published" ? "Pubblica modifiche" : "Pubblica";

  return (
    <div data-testid="admin-article-editor">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl tracking-tight">{isNew ? "Nuovo articolo" : "Modifica articolo"}</h1>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => nav("/growing-circle/admin/articles")}><X className="w-4 h-4 mr-1" />Chiudi</Button>
          {!isNew && <Button variant="ghost" onClick={openPreview} data-testid="btn-preview"><Eye className="w-4 h-4 mr-1" />Anteprima</Button>}
          <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="btn-save-draft"><Save className="w-4 h-4 mr-1" />Salva bozza</Button>
          <Button onClick={publish} className="btn-gold" data-testid="btn-publish"><Send className="w-4 h-4 mr-1" />{canPublishBtnLabel}</Button>
        </div>
      </div>

      {state && (
        <div className="mb-4 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.12em] text-muted-foreground">
          <span>Stato: <strong className="text-foreground">{state}</strong></span>
          {state === "published" && article?.draft_revision_id && <span className="text-amber-500">Bozza modifiche in corso</span>}
          {state === "published" && article?.draft_revision_id && (
            <button className="underline" onClick={() => doAction("discard-draft", "Scartare le modifiche in bozza?")}
                    data-testid="btn-discard-draft">Scarta modifiche</button>
          )}
          {state === "published" && <button className="underline" onClick={() => doAction("unpublish")} data-testid="btn-unpublish"><EyeOff className="w-3 h-3 inline mr-1" />Rimuovi dalla pubblicazione</button>}
          {state !== "archived" && <button className="underline" onClick={() => doAction("archive", "Archiviare l'articolo? I commenti e le revisioni saranno preservate.")} data-testid="btn-archive"><Archive className="w-3 h-3 inline mr-1" />Archivia</button>}
          {state === "archived" && <button className="underline" onClick={() => doAction("restore")} data-testid="btn-restore">Ripristina dall'archivio</button>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <div>
            <Label>Serie</Label>
            <Input value={fields.series_name} onChange={(e) => setFields({ ...fields, series_name: e.target.value })} data-testid="fld-series" />
          </div>
          <div>
            <Label>Titolo *</Label>
            <Textarea
              rows={2}
              value={fields.title}
              onChange={(e) => setFields({ ...fields, title: e.target.value })}
              placeholder="Premi Invio per andare a capo dove vuoi che si spezzi il titolo."
              data-testid="fld-title"
              className="text-lg font-semibold leading-snug"
            />
          </div>
          <div>
            <Label>Sottotitolo</Label>
            <Input value={fields.subtitle} onChange={(e) => setFields({ ...fields, subtitle: e.target.value })} data-testid="fld-subtitle" />
          </div>
          <div>
            <Label>Credenziali autore (mostrate sotto il nome)</Label>
            <Textarea rows={2} value={fields.author_credentials} onChange={(e) => setFields({ ...fields, author_credentials: e.target.value })} data-testid="fld-credentials" />
          </div>
          <div>
            <Label>Nota di apertura (opzionale)</Label>
            <TipTapEditor value={fields.opening_note} onChange={(json) => setFields({ ...fields, opening_note: json })} compact placeholder="Testo introduttivo mostrato prima del corpo, con lo stesso stile del corpo." />
          </div>
          <div>
            <Label>Corpo articolo</Label>
            <TipTapEditor value={fields.body} onChange={(json) => setFields({ ...fields, body: json })} />
          </div>
          <div>
            <Label>Nota autore / biografia (opzionale)</Label>
            <Textarea rows={4} value={fields.author_note_or_biography} onChange={(e) => setFields({ ...fields, author_note_or_biography: e.target.value })} data-testid="fld-bio" />
          </div>
          <div>
            <Label>Nota di copyright (opzionale)</Label>
            <Textarea rows={2} value={fields.copyright_note} onChange={(e) => setFields({ ...fields, copyright_note: e.target.value })} placeholder="© 2026 Maria Gioia Mosca. Tutti i diritti riservati." data-testid="fld-copyright" />
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto lg:pr-1 h-fit">
          <div className="bg-card border border-border rounded-xl p-4">
            <Label>Copertina</Label>
            {fields.cover_media_id ? (
              <div className="mt-2">
                <img src={`${BACKEND}/api/growing-circle/article-media/${fields.cover_media_id}`} alt="" className="w-full rounded" />
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => setFields({ ...fields, cover_media_id: null })} data-testid="btn-remove-cover">
                  <Trash2 className="w-3 h-3 mr-1" />Rimuovi
                </Button>
              </div>
            ) : (
              <label className="mt-2 block cursor-pointer text-xs uppercase tracking-[0.12em] text-primary underline">
                Carica immagine
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="input-cover" />
              </label>
            )}
            <div className="mt-3">
              <Label className="text-xs">Testo alternativo copertina</Label>
              <Input value={fields.cover_alt_text} onChange={(e) => setFields({ ...fields, cover_alt_text: e.target.value })} disabled={fields.cover_decorative} data-testid="fld-cover-alt" />
              <label className="flex items-center gap-2 mt-2 text-xs">
                <input type="checkbox" checked={fields.cover_decorative} onChange={(e) => setFields({ ...fields, cover_decorative: e.target.checked })} data-testid="fld-cover-decorative" />
                Immagine decorativa (nessun testo alternativo)
              </label>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <Label>Data di pubblicazione</Label>
            <Input type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} data-testid="fld-publish-at" />
            <p className="text-xs text-muted-foreground mt-1">Se futura, l'articolo appare automaticamente ai membri a quella data (fuso orario Europa/Roma).</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <Label>Richiedi approvazione commenti</Label>
              <Switch checked={moderation} onCheckedChange={setModeration} data-testid="fld-moderation" />
            </div>
          </div>

          {(!article?.first_published_at) && (
            <div className="bg-card border border-border rounded-xl p-4">
              <Label>URL articolo</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-generato dal titolo" data-testid="fld-slug" />
              <p className="text-xs text-muted-foreground mt-1">Modificabile solo prima della prima pubblicazione.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
